import * as React from 'react'
import { useNavigate } from 'react-router-dom'
import { useQueryTenantMe } from '../api/useQueryTenantMe'
import { useMutationBootstrapTenant } from '../api/useMutationBootstrapTenant'
import { useLocalize } from '@/modules/shared/context/localize/LocalizeContext'
import type { BootstrapAccountType } from '../types'
import { FaIcon, faBuildingSolid, faBuildingsSolid } from '@/lib/fa'
import { useMutateGusData } from '@/modules/settings/react-query/basic/useMutateGusData'

export const PostRegisterPage: React.FC = () => {
  const { t } = useLocalize()
  const navigate = useNavigate()
  const { data: existing, isLoading } = useQueryTenantMe()
  const bootstrap = useMutationBootstrapTenant()
  const gus = useMutateGusData('')

  const [accountType, setAccountType] =
    React.useState<BootstrapAccountType>('StandardCompany')
  const [companyName, setCompanyName] = React.useState('')
  const [nip, setNip] = React.useState('')
  const [street, setStreet] = React.useState('')
  const [city, setCity] = React.useState('')
  const [postalCode, setPostalCode] = React.useState('')

  React.useEffect(() => {
    if (existing) {
      navigate(`/checkout?companyId=${existing.companyId}`, { replace: true })
    }
  }, [existing, navigate])

  const handleNipChange = async (value: string) => {
    setNip(value)
    const cleanNip = value.replace(/\D/g, '')
    if (cleanNip.length !== 10 || gus.isPending) return

    const res = await gus.mutateAsync({ nip: cleanNip })
    if (res.name) setCompanyName(res.name)
    if (res.nip) setNip(res.nip)
    if (res.street) setStreet(res.street)
    if (res.postalCode) setPostalCode(res.postalCode)
    if (res.city) setCity(res.city)
  }

  const onSubmit = async (e: React.FormEvent) => {
    e.preventDefault()
    if (!companyName.trim()) return
    const res = await bootstrap.mutateAsync({
      companyName: companyName.trim(),
      accountType,
      nip: nip.trim() || undefined,
      street: street.trim() || undefined,
      city: city.trim() || undefined,
      postalCode: postalCode.trim() || undefined,
    })
    navigate(`/checkout?companyId=${res.companyId}`)
  }

  if (isLoading) return <div style={{ padding: 24 }}>{t('subscription.loading')}</div>

  return (
    <div style={pageStyle}>
      <h1 style={titleStyle}>{t('subscription.postRegister.title')}</h1>
      <p style={subtitleStyle}>
        {t('subscription.postRegister.subtitle')}
      </p>

      <form onSubmit={onSubmit} style={formStyle}>
        <div>
          <div style={sectionLabelStyle}>
            {t('subscription.postRegister.accountType')}
          </div>
          <div style={accountTypeGridStyle}>
            <button
              type="button"
              onClick={() => setAccountType('StandardCompany')}
              style={{
                ...accountTypeButtonStyle,
                ...(accountType === 'StandardCompany'
                  ? accountTypeButtonActiveStyle
                  : {}),
              }}
            >
              <span
                style={{
                  ...accountTypeIconStyle,
                  ...(accountType === 'StandardCompany'
                    ? accountTypeIconActiveStyle
                    : {}),
                }}
              >
                <FaIcon icon={faBuildingSolid} />
              </span>
              <span style={accountTypeContentStyle}>
                <span style={accountTypeTitleStyle}>
                  {t('subscription.postRegister.standardCompanyTitle')}
                </span>
                <span style={accountTypeDescriptionStyle}>
                  {t('subscription.postRegister.standardCompanyDescription')}
                </span>
              </span>
            </button>
            <button
              type="button"
              onClick={() => setAccountType('ServiceCompany')}
              style={{
                ...accountTypeButtonStyle,
                ...(accountType === 'ServiceCompany'
                  ? accountTypeButtonActiveStyle
                  : {}),
              }}
            >
              <span
                style={{
                  ...accountTypeIconStyle,
                  ...(accountType === 'ServiceCompany'
                    ? accountTypeIconActiveStyle
                    : {}),
                }}
              >
                <FaIcon icon={faBuildingsSolid} />
              </span>
              <span style={accountTypeContentStyle}>
                <span style={accountTypeTitleStyle}>
                  {t('subscription.postRegister.serviceCompanyTitle')}
                </span>
                <span style={accountTypeDescriptionStyle}>
                  {t('subscription.postRegister.serviceCompanyDescription')}
                </span>
              </span>
            </button>
          </div>
        </div>

        <label>
          {t('subscription.postRegister.companyName')} *
          <input
            type="text"
            value={companyName}
            onChange={(e) => setCompanyName(e.target.value)}
            required
            style={inputStyle}
          />
        </label>

        <label>
          {t('subscription.postRegister.nip')}
          <input
            type="text"
            value={nip}
            onChange={(e) => handleNipChange(e.target.value)}
            placeholder={t('subscription.postRegister.nipPlaceholder')}
            style={inputStyle}
          />
          {gus.isPending && (
            <span style={helpTextStyle}>
              {t('addCompany.fetchingGusData')}
            </span>
          )}
        </label>

        <label>
          {t('subscription.postRegister.street')}
          <input
            type="text"
            value={street}
            onChange={(e) => setStreet(e.target.value)}
            style={inputStyle}
          />
        </label>

        <div style={addressGridStyle}>
          <label>
            {t('subscription.postRegister.postalCode')}
            <input
              type="text"
              value={postalCode}
              onChange={(e) => setPostalCode(e.target.value)}
              style={inputStyle}
            />
          </label>
          <label>
            {t('subscription.postRegister.city')}
            <input
              type="text"
              value={city}
              onChange={(e) => setCity(e.target.value)}
              style={inputStyle}
            />
          </label>
        </div>

        {bootstrap.error && (
          <div style={{ color: '#b91c1c', fontSize: 13 }}>
            {t('subscription.postRegister.error')}
          </div>
        )}

        <button
          type="submit"
          disabled={bootstrap.isPending || !companyName.trim()}
          style={primaryButtonStyle}
        >
          {bootstrap.isPending
            ? t('subscription.postRegister.submitting')
            : t('subscription.postRegister.submit')}
        </button>
      </form>
    </div>
  )
}

const pageStyle: React.CSSProperties = {
  width: '100%',
  maxWidth: 760,
  margin: '56px auto',
  padding: '0 24px',
}

const titleStyle: React.CSSProperties = {
  marginBottom: 8,
  color: '#111827',
  fontSize: 28,
  fontWeight: 700,
}

const subtitleStyle: React.CSSProperties = {
  color: '#6b7280',
  marginBottom: 28,
  fontSize: 15,
  lineHeight: 1.45,
}

const formStyle: React.CSSProperties = {
  display: 'grid',
  gap: 14,
}

const sectionLabelStyle: React.CSSProperties = {
  fontWeight: 700,
  marginBottom: 10,
  color: '#111827',
}

const accountTypeGridStyle: React.CSSProperties = {
  display: 'grid',
  gridTemplateColumns: 'repeat(auto-fit, minmax(240px, 1fr))',
  gap: 12,
}

const accountTypeButtonStyle: React.CSSProperties = {
  display: 'flex',
  alignItems: 'flex-start',
  gap: 14,
  width: '100%',
  minHeight: 118,
  padding: '18px 18px',
  border: '1px solid #d8deea',
  borderRadius: 8,
  background: '#fff',
  color: '#111827',
  textAlign: 'left',
  cursor: 'pointer',
  transition:
    'border-color 160ms ease, box-shadow 160ms ease, background 160ms ease',
}

const accountTypeButtonActiveStyle: React.CSSProperties = {
  borderColor: '#2448bb',
  boxShadow: '0 0 0 1px #2448bb, 0 12px 28px rgba(36, 72, 187, 0.12)',
  background: '#f7f9ff',
}

const accountTypeIconStyle: React.CSSProperties = {
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  width: 42,
  height: 42,
  flexShrink: 0,
  borderRadius: 8,
  background: '#eef2ff',
  color: '#2448bb',
  fontSize: 20,
}

const accountTypeIconActiveStyle: React.CSSProperties = {
  background: '#2448bb',
  color: '#fff',
}

const accountTypeContentStyle: React.CSSProperties = {
  display: 'grid',
  gap: 6,
  minWidth: 0,
}

const accountTypeTitleStyle: React.CSSProperties = {
  fontSize: 15,
  fontWeight: 700,
  lineHeight: 1.25,
}

const accountTypeDescriptionStyle: React.CSSProperties = {
  color: '#6b7280',
  fontSize: 13,
  lineHeight: 1.45,
}

const addressGridStyle: React.CSSProperties = {
  display: 'grid',
  gridTemplateColumns: 'repeat(auto-fit, minmax(180px, 1fr))',
  gap: 12,
}

const inputStyle: React.CSSProperties = {
  display: 'block',
  width: '100%',
  padding: '8px 10px',
  border: '1px solid #d1d5db',
  borderRadius: 6,
  marginTop: 4,
  fontSize: 14,
}

const helpTextStyle: React.CSSProperties = {
  display: 'block',
  marginTop: 4,
  color: '#6b7280',
  fontSize: 12,
}

const primaryButtonStyle: React.CSSProperties = {
  padding: '10px 16px',
  background: '#2563eb',
  color: '#fff',
  border: 'none',
  borderRadius: 6,
  fontSize: 14,
  fontWeight: 600,
  cursor: 'pointer',
  marginTop: 8,
}
